import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import AttachmentsComponent from "@features/vessel-call/vessel-call-status/components/AttachmentComponent/AttachmentsComponent";
import { formatDateTime } from "@features/vessel-call/vessel-call-status/utils";
import { useDownloadCertificate } from "@pages/vessel-management/vessel-status/hooks/useDownloadCertificate";
import { translateNormalizedRole } from "@utils/roles";
import { t } from "i18next";
import { LANG_BASE } from "../../ShippingAgencyTransfer";

type DetailsSideDrawerProps = {
  isOpen: boolean;
  testId: string;
  onClose: () => void;
  rejectionReason: string;
  externalDocument?: string;
  externalDocumentName?: string;
  submittedBy?: string;
  submittedByRole?: string;
  submittedAt?: string;
};

const service = "bff-vessel-call";

const DetailsSideDrawer: React.FC<DetailsSideDrawerProps> = ({
  isOpen,
  testId,
  onClose,
  rejectionReason,
  externalDocument,
  externalDocumentName,
  submittedBy,
  submittedByRole,
  submittedAt,
}) => {
  const { handleDownload, handlePreview } = useDownloadCertificate();
  const submittedByName = translateNormalizedRole(t, submittedBy);
  const submittedByRoleText = translateNormalizedRole(t, submittedByRole);
  const hasSubmittedByInfo = Boolean(submittedByName || submittedByRoleText);
  const submittedAtText = submittedAt ? formatDateTime(submittedAt) : "-";

  return (
    <div data-testid={`${testId}-container`} className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        title={t(`${LANG_BASE}.status-drawer-title`)}
        onClose={onClose}
        submitLabel={t("common.ok")}
        onSubmit={onClose}
        hideCancelButton={true}
      >
        <div className="flex flex-col gap-4 w-[400px] p-6">
          <div className="col-span-full flex flex-col gap-2">
            <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }} data-testid="labelId">
              {t(`${LANG_BASE}.rejection-reason`)}
            </CustomFormLabel>
            <div
              className="text-sm whitespace-pre-wrap wrap-break-word"
              data-testid={`${testId}-static-content`}
            >
              {rejectionReason}
            </div>
          </div>
          {externalDocument && (
            <AttachmentsComponent
              data-testid={`${testId}-external-attachment`}
              documentName={externalDocumentName ?? ""}
              onDownload={() => {
                handleDownload("document", service, externalDocument, externalDocumentName);
              }}
              onPreview={() => handlePreview("document", service, externalDocument)}
              langBase={LANG_BASE}
            />
          )}
          <div>
            <p className="text-xs text-gray-500">
              <span>{t("vessel-call.interruption.submittedBy")}:</span>{" "}
              {hasSubmittedByInfo ? (
                <>
                  {submittedByName && <span className="text-gray-800">{submittedByName}</span>}
                  {submittedByName && submittedByRoleText ? " / " : null}
                  {submittedByRoleText}
                </>
              ) : (
                "-"
              )}
            </p>
            <p className="text-xs text-gray-500 mt-1">{submittedAtText}</p>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default DetailsSideDrawer;
